"use client"

/**
 * Multi-account device session storage.
 *
 * Up to MAX_ACCOUNTS authenticated identities can coexist on one device.
 * One is "active" — its token mirrors to the legacy `AskFinz_auth_token`
 * key + cross-subdomain cookie so existing auth code works unchanged.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * SECURITY V-029 / V-061 — DEPRECATED localStorage WRITES
 * ─────────────────────────────────────────────────────────────────────────
 * As of 2026-05-09 the active JWT is also set by the backend as an
 * HttpOnly+Secure+SameSite=Lax cookie scoped to .askfinz.ai. That cookie
 * is the canonical, server-trusted source for API authentication.
 *
 * The localStorage writes below are TEMPORARILY KEPT because:
 *   - We need a JS-readable copy of EACH inactive identity's JWT to switch
 *     between accounts (the browser only sends the active HttpOnly cookie).
 *   - Cross-subdomain handoff still uses `?add_token=` (V-008 wave will
 *     replace this with one-shot HMAC handoff codes; once that lands the
 *     receiving app no longer needs to write to localStorage at all).
 *
 * TODO (V-008 wave):
 *   - Replace stored per-identity tokens with stored per-identity refresh-
 *     token cookies (one HttpOnly cookie per identity, switch via a
 *     server-side `/api/auth/switch?id=...` that swaps the active cookie).
 *   - Then remove every `localStorage.setItem("AskFinz_auth_token", ...)`,
 *     every JWT write to AccountEntry.token, and the entire ACCOUNTS_COOKIE
 *     code path (it leaks JWTs in a non-HttpOnly cookie).
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Cross-subdomain semantics:
 *   - The "active" identity is synced across *.askfinz.ai via cookie.
 *   - The "list of added accounts" is per-subdomain (localStorage is per-origin).
 *     `getAccounts()` self-heals by appending the cookie-active token if it isn't
 *     in the list yet — so switching active on subdomain A and visiting subdomain
 *     B silently picks up the new active identity in B's list.
 *
 * The token-add cross-subdomain handoff uses `?add_token=` in the URL (vs.
 * `?token=` for plain replace). See `getAuthToken()` in lib/auth.ts.
 */

import { clearAuthToken, setAuthCookie } from "./auth"

export const MAX_ACCOUNTS = 4

const ACCOUNTS_KEY = "AskFinz_accounts"
const ACTIVE_KEY = "AskFinz_active_account_id"
const COOKIE_NAME = "AskFinz_auth_token"

// Shared-across-subdomains cookies (Domain=.askfinz.ai). Carry the canonical
// device account list + active id so every *.askfinz.ai page sees the same
// state. Per-cookie size budget is ~4KB; with MAX_ACCOUNTS=4 and JWT+metadata
// at ~500 bytes each we land around 2-2.5KB — comfortably under.
const ACCOUNTS_COOKIE = "AskFinz_accounts_v1"
const ACTIVE_COOKIE = "AskFinz_active_account_v1"
const SHARED_COOKIE_MAX_AGE = 7 * 24 * 60 * 60   // 7 days

export interface AccountUser {
    name?: string
    email?: string
    avatar?: string
    [k: string]: any
}

export interface AccountEntry {
    id: string
    token: string
    user: AccountUser
    addedAt: number
}

function _safeParse<T>(raw: string | null, fallback: T): T {
    if (!raw) return fallback
    try { return JSON.parse(raw) as T } catch { return fallback }
}

function _hash(input: string): string {
    let h = 0x811c9dc5
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i)
        h = Math.imul(h, 0x01000193) >>> 0
    }
    return h.toString(16).padStart(8, "0")
}

/** Return true iff the token looks like a real JWT (3 base64url parts starting
 * with "eyJ"). Used to guard legacy storage writes so placeholder strings like
 * "handoff:<identity>" are never persisted as Bearer tokens. */
function _isJwt(token: string): boolean {
    if (!token) return false
    const parts = token.split(".")
    return parts.length === 3 && token.startsWith("eyJ")
}

/** Decode a JWT payload (base64url) without verification — used only to
 * extract a stable identity key for dedup. Returns null on malformed input. */
function _decodeJwtPayload(token: string): Record<string, any> | null {
    try {
        const parts = token.split(".")
        if (parts.length < 2) return null
        const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/")
        const padded = b64 + "===".slice((b64.length + 3) % 4)
        const json = atob(padded)
        // Decode UTF-8 properly (atob returns binary string)
        const decoded = decodeURIComponent(Array.from(json).map(c =>
            "%" + c.charCodeAt(0).toString(16).padStart(2, "0")
        ).join(""))
        return JSON.parse(decoded)
    } catch {
        return null
    }
}

/** Stable per-identity id. Derived from email (canonical, human-readable,
 * present in every entry). Fallbacks for entries without an email: JWT
 * `user_id` claim, then `sub`, then a token hash (last resort, will only
 * produce duplicates when no identity is recoverable at all).
 *
 * Why email-first: the same user logging in multiple times produces
 * different JWTs but always the same email. Hashing on email guarantees
 * the same human collapses to one entry regardless of token format
 * (JWT vs opaque session ID vs anything else).
 */
function _identityId(token: string, user?: AccountUser): string {
    const payload = _decodeJwtPayload(token) ?? {}
    const email = String(user?.email || payload.email || "").trim().toLowerCase()
    if (email) return _hash(`email:${email}`)
    const uid = String(payload.user_id || payload.sub || "").trim().toLowerCase()
    if (uid) return _hash(`uid:${uid}`)
    // Last resort: token-hash (legacy behaviour). Only reached when neither
    // email nor user_id is recoverable.
    return _hash(`tok:${token}`)
}

/** Backwards-compat alias. Older entries used a token-derived id; new
 * code uses _identityId. We keep this exported in case external callers
 * referenced it. */
function _hashToken(token: string): string {
    return _identityId(token)
}

function _getCookie(name: string): string | null {
    if (typeof document === "undefined") return null
    const nameEQ = name + "="
    const ca = document.cookie.split(";")
    for (let c of ca) {
        c = c.trimStart()
        if (c.startsWith(nameEQ)) return decodeURIComponent(c.substring(nameEQ.length))
    }
    return null
}

function _userFromCookies(): AccountUser {
    return {
        name: _getCookie("AskFinz_user_name") || "User",
        email: _getCookie("AskFinz_user_email") || "",
        avatar: _getCookie("AskFinz_user_avatar") || "",
    }
}

function _domainAttr(): string {
    if (typeof window === "undefined") return ""
    return window.location.hostname.endsWith("askfinz.ai") ? "; Domain=.askfinz.ai" : ""
}

function _writeSharedCookie(name: string, value: string, maxAge: number = SHARED_COOKIE_MAX_AGE) {
    if (typeof document === "undefined") return
    const v = encodeURIComponent(value)
    document.cookie = `${name}=${v}; path=/; max-age=${maxAge}; SameSite=Lax${_domainAttr()}`
}

function _deleteSharedCookie(name: string) {
    if (typeof document === "undefined") return
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${_domainAttr()}`
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

/** Read the cross-subdomain accounts cookie (canonical source of truth). */
function _readSharedAccountsCookie(): AccountEntry[] {
    const raw = _getCookie(ACCOUNTS_COOKIE)
    if (!raw) return []
    const parsed = _safeParse<AccountEntry[]>(raw, [])
    return Array.isArray(parsed) ? parsed.filter(a => a && a.token && a.id) : []
}

/** Persist the accounts list. Writes to BOTH the cross-subdomain cookie
 * (so every *.askfinz.ai page sees the same list) AND localStorage (legacy
 * read path + offline resilience). */
function _persistAccounts(list: AccountEntry[]) {
    const trimmed = list.slice(-MAX_ACCOUNTS)
    try { localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(trimmed)) } catch { /* ignore */ }
    try {
        const json = JSON.stringify(trimmed)
        // Browser per-cookie limit is ~4KB. If we exceed, drop avatars first
        // (recoverable from the user/me API), then fall back to localStorage-only.
        let payload = json
        if (json.length > 3500) {
            const stripped = trimmed.map(a => ({ ...a, user: { ...a.user, avatar: "" } }))
            payload = JSON.stringify(stripped)
        }
        if (payload.length > 3800) {
            // Last resort: drop the cookie so we don't truncate it badly
            _deleteSharedCookie(ACCOUNTS_COOKIE)
            return
        }
        _writeSharedCookie(ACCOUNTS_COOKIE, payload)
    } catch { /* ignore */ }
}

/** Migrate legacy token-derived ids to identity-derived ids and dedup
 * entries that point at the same human user. Returns the cleaned list.
 *
 * Older builds (v1 of this module) hashed the JWT itself to produce the
 * `id`, so re-logging in as the same user appended a new entry with a
 * different id every time. Newer builds derive `id` from the JWT's
 * `user_id` claim — same human → same id. This pass:
 *   1. Recomputes every entry's id from its current token/user.
 *   2. Collapses duplicates: keeps the most-recently-added entry per id,
 *      taking that entry's `token` (newest is freshest) and merging
 *      `user` metadata.
 */
function _migrateAndDedup(list: AccountEntry[]): { list: AccountEntry[]; changed: boolean } {
    let changed = false
    const byId = new Map<string, AccountEntry>()
    // Process in chronological order so the latest addedAt wins on collision
    const ordered = [...list].sort((a, b) => (a.addedAt ?? 0) - (b.addedAt ?? 0))
    for (const e of ordered) {
        if (!e || !e.token) continue
        const correctId = _identityId(e.token, e.user)
        if (correctId !== e.id) changed = true
        const existing = byId.get(correctId)
        if (existing) {
            // Same human user — keep the freshest token + merged user
            existing.token = e.token
            existing.user = { ...existing.user, ...e.user }
            existing.addedAt = e.addedAt ?? existing.addedAt
            changed = true
        } else {
            byId.set(correctId, { ...e, id: correctId })
        }
    }
    return { list: Array.from(byId.values()), changed }
}

/**
 * Read the per-device accounts list.
 *
 * Order of precedence:
 *   1. Cross-subdomain cookie (`AskFinz_accounts_v1`, Domain=.askfinz.ai) —
 *      canonical source so every *.askfinz.ai page sees the same list.
 *   2. localStorage (legacy / per-subdomain mirror).
 *   3. Migrate ids from token-hash to identity-hash + dedup.
 *   4. Self-heal: append the legacy active token if missing.
 *
 * Whatever's read is mirrored back to localStorage for offline access.
 */
export function getAccounts(): AccountEntry[] {
    if (typeof window === "undefined") return []

    // 1. Prefer the shared cookie — it's the source of truth across subdomains.
    const cookieList = _readSharedAccountsCookie()
    let list: AccountEntry[]
    if (cookieList.length > 0) {
        list = cookieList
    } else {
        // 2. Fall back to localStorage.
        const lsList = _safeParse<AccountEntry[]>(localStorage.getItem(ACCOUNTS_KEY), [])
        list = Array.isArray(lsList) ? lsList.filter(a => a && a.token && a.id) : []
    }

    // 3. Migrate + dedup against pre-existing token-hash ids
    const { list: migrated, changed } = _migrateAndDedup(list)
    list = migrated
    if (changed) _persistAccounts(list)

    // 4. Self-heal: append the legacy active token if its identity isn't already in the list.
    const legacyToken = localStorage.getItem("AskFinz_auth_token") || _getCookie(COOKIE_NAME)
    if (legacyToken) {
        const userRaw = localStorage.getItem("AskFinz_auth_user")
        const user: AccountUser = _safeParse<AccountUser | null>(userRaw, null) || _userFromCookies()
        const legacyId = _identityId(legacyToken, user)
        const existing = list.find(a => a.id === legacyId)
        if (!existing) {
            list.push({ id: legacyId, token: legacyToken, user, addedAt: Date.now() })
            _persistAccounts(list)
            try {
                if (!localStorage.getItem(ACTIVE_KEY)) {
                    localStorage.setItem(ACTIVE_KEY, legacyId)
                    _writeSharedCookie(ACTIVE_COOKIE, legacyId)
                }
            } catch { /* ignore */ }
        } else if (existing.token !== legacyToken) {
            // Same identity, fresher token (this subdomain re-logged in).
            // Update the entry's token rather than duplicating.
            existing.token = legacyToken
            existing.user = { ...existing.user, ...user }
            _persistAccounts(list)
        }
    } else if (cookieList.length > 0 && !changed) {
        // Mirror the cookie list back to localStorage for offline resilience.
        try { localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(list)) } catch { /* ignore */ }
    }
    return list
}

export function getActiveAccountId(): string | null {
    if (typeof window === "undefined") return null
    // Cookie wins over localStorage so subdomain-switching propagates.
    return _getCookie(ACTIVE_COOKIE) || localStorage.getItem(ACTIVE_KEY)
}

export function getActiveAccount(): AccountEntry | null {
    const list = getAccounts()
    const id = getActiveAccountId()
    if (id) {
        const m = list.find(a => a.id === id)
        if (m) return m
    }
    return list[list.length - 1] ?? null
}

/**
 * Add a freshly-issued token + user to the device account list and mark it
 * active. Mirrors to the legacy auth keys + cross-subdomain cookie so the
 * rest of the app behaves identically.
 *
 * If the token already exists in the list (re-login), it is updated in
 * place rather than duplicated.
 *
 * Returns the entry that ended up in the list (with its id).
 */
export function addAccount(token: string, user: AccountUser): AccountEntry {
    if (typeof window === "undefined") {
        return { id: "", token, user, addedAt: Date.now() }
    }
    const list = getAccounts()
    const newId = _identityId(token, user)
    // Dedup by identity-id (same human user across re-logins) AND by exact
    // token (same JWT being re-added). A re-login as the same user replaces
    // the existing entry's token + refreshes user metadata in place.
    let entry = list.find(a => a.id === newId || a.token === token)
    if (entry) {
        entry.id = newId
        entry.token = token
        entry.user = { ...entry.user, ...user }
        entry.addedAt = Date.now()
    } else {
        entry = { id: newId, token, user, addedAt: Date.now() }
        list.push(entry)
        // Cap to MAX_ACCOUNTS — drop the oldest non-active entry
        if (list.length > MAX_ACCOUNTS) {
            const activeId = getActiveAccountId()
            const trimmed: AccountEntry[] = []
            // Always keep the active one + N most recent
            const sorted = [...list].sort((a, b) => b.addedAt - a.addedAt)
            for (const a of sorted) {
                if (a.id === activeId || trimmed.length < MAX_ACCOUNTS - (activeId && !trimmed.some(t => t.id === activeId) ? 1 : 0)) {
                    trimmed.push(a)
                    if (trimmed.length >= MAX_ACCOUNTS) break
                }
            }
            list.length = 0
            list.push(...trimmed)
        }
    }
    _persistAccounts(list)
    setActiveAccount(entry.id)
    return entry
}

/**
 * Switch active. Updates legacy keys + cross-subdomain cookie + dispatches
 * `authUserUpdated` so listening components re-read.
 *
 * Performs the legacy/cookie writes inline (rather than calling
 * `setAuthToken`) to avoid a recursion loop — `setAuthToken` itself calls
 * `addAccount` to keep the list in sync, which would re-enter here.
 */
export function setActiveAccount(id: string): boolean {
    if (typeof window === "undefined") return false
    const list = getAccounts()
    const entry = list.find(a => a.id === id)
    if (!entry) return false
    // SECURITY V-008: only write the token to legacy storage when it is a
    // real JWT. Placeholder strings like "handoff:<identity>" must never reach
    // localStorage or the JS-readable cookie — the backend prefers the Bearer
    // header over the HttpOnly cookie, so a fake Bearer causes a 401 that
    // triggers redirectToLoginApp() even though the HttpOnly cookie is valid.
    const isJwt = _isJwt(entry.token)
    try {
        localStorage.setItem(ACTIVE_KEY, id)
        if (isJwt) {
            localStorage.setItem("AskFinz_auth_token", entry.token)
        }
        if (entry.user) {
            localStorage.setItem("AskFinz_auth_user", JSON.stringify(entry.user))
        }
    } catch { /* ignore */ }
    // Cross-subdomain active id cookie (Domain=.askfinz.ai)
    _writeSharedCookie(ACTIVE_COOKIE, id)
    // Only call setAuthCookie for real JWTs — setAuthCookie writes
    // AskFinz_auth_token to a JS cookie on localhost (dev) and overwrites the
    // session marker with a hash of whatever token we pass. A fake placeholder
    // here would corrupt both, causing auth failures on the next API call.
    try { if (isJwt) setAuthCookie(entry.token) } catch { /* ignore */ }
    // Set the shared display cookies so other subdomains can read user info
    // off the cookie before they get a chance to fetch /api/auth/me.
    if (typeof document !== "undefined" && entry.user) {
        const maxAge = 7 * 24 * 60 * 60
        const domainAttr = (typeof window !== "undefined" && window.location.hostname.endsWith("askfinz.ai")) ? "; Domain=.askfinz.ai" : ""
        const setShared = (name: string, val: string) => {
            document.cookie = `${name}=${encodeURIComponent(val)}; path=/; max-age=${maxAge}; SameSite=Lax${domainAttr}`
        }
        if (entry.user.name) setShared("AskFinz_user_name", entry.user.name)
        if (entry.user.email) setShared("AskFinz_user_email", entry.user.email)
        if (entry.user.avatar) setShared("AskFinz_user_avatar", entry.user.avatar)
    }
    try {
        window.dispatchEvent(new CustomEvent("authUserUpdated", { detail: entry.user ?? null }))
    } catch { /* ignore */ }
    return true
}

/**
 * Remove an account from the device list.
 *
 *   - If `id` was the active account: switch active to the most recently
 *     added remaining one. If none remain, fully clear auth.
 *   - Best-effort revoke server-side via /api/auth/logout (caller can pass
 *     `revokeOnServer: false` to skip).
 */
export async function removeAccount(id: string, opts?: { revokeOnServer?: boolean }): Promise<{ removed: boolean; nextActiveId: string | null }> {
    if (typeof window === "undefined") return { removed: false, nextActiveId: null }
    const list = getAccounts()
    const idx = list.findIndex(a => a.id === id)
    if (idx < 0) return { removed: false, nextActiveId: getActiveAccountId() }
    const entry = list[idx]

    // Best-effort server-side revoke for this specific token
    if (opts?.revokeOnServer !== false) {
        try {
            const { getAccountApiUrl } = await import("./auth")
            // SECURITY V-028: /logout requires the X-CSRF-Token header.
            // Read the AskFinz_csrf cookie set by the server at login.
            let csrf = ""
            if (typeof document !== "undefined") {
                const m = document.cookie.match(/(?:^|;\s*)AskFinz_csrf=([^;]+)/)
                csrf = m ? decodeURIComponent(m[1]) : ""
            }
            const headers: Record<string, string> = { Authorization: `Bearer ${entry.token}` }
            if (csrf) headers["X-CSRF-Token"] = csrf
            await fetch(`${getAccountApiUrl()}/api/auth/logout`, {
                method: "POST",
                headers,
                credentials: "include",
            }).catch(() => { /* network blip — ignore */ })
        } catch { /* ignore */ }
    }

    list.splice(idx, 1)
    _persistAccounts(list)

    const wasActive = getActiveAccountId() === id
    if (wasActive) {
        if (list.length > 0) {
            // Switch to the most recently added remaining
            const next = [...list].sort((a, b) => b.addedAt - a.addedAt)[0]
            setActiveAccount(next.id)
            return { removed: true, nextActiveId: next.id }
        }
        // No accounts left — full clear
        try { localStorage.removeItem(ACTIVE_KEY) } catch { /* ignore */ }
        _deleteSharedCookie(ACTIVE_COOKIE)
        _deleteSharedCookie(ACCOUNTS_COOKIE)
        clearAuthToken()
        return { removed: true, nextActiveId: null }
    }
    return { removed: true, nextActiveId: getActiveAccountId() }
}

/** Wipe every account from the device. Used by full sign-out. */
export function clearAllAccounts(): void {
    if (typeof window === "undefined") return
    try { localStorage.removeItem(ACCOUNTS_KEY) } catch { /* ignore */ }
    try { localStorage.removeItem(ACTIVE_KEY) } catch { /* ignore */ }
    _deleteSharedCookie(ACCOUNTS_COOKIE)
    _deleteSharedCookie(ACTIVE_COOKIE)
    clearAuthToken()
}

/**
 * Build the URL the "+" button should navigate to. The login app reads
 * `add_account=1` and skips the global setAuthToken, redirecting back
 * with `?add_token=` instead of `?token=`.
 */
export function buildAddAccountUrl(loginAppUrl: string, returnTo: string): string {
    const u = new URL(loginAppUrl.replace(/\/$/, "") + "/auth/login")
    u.searchParams.set("add_account", "1")
    u.searchParams.set("redirect", returnTo)
    return u.toString()
}
